import { useEffect } from 'react';

import { Box, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import { noop } from '@altirllc/shared/utils/functionalUtils';

import { useModals } from '@/contexts/ModalsProvider';

import { formatPhone, toFullName } from '@/utils/stringUtils';

import { MemberStatus } from '@/components/MemberStatus';
import { MemberMenuCell } from '@/components/table/bodyCells/MemberMenuCell';
import { UserAvatar } from '@/components/UserAvatar';

import type { TEditMemberForm, TMember } from '@/types/member';

import { styles } from '../MemberModal.styles';
import MemberTable from '../MemberTable';

type TEditMemberFieldsProps = {
  member: TMember;
  onSuccess: (member: TMember) => void;
};

export const MemberDetailsFields = ({ member, onSuccess }: TEditMemberFieldsProps) => {
  const { values, setFieldValue } = useFormikContext<TEditMemberForm>();
  const { open } = useModals();

  const actions = {
    cancel: noop,

    edit: () => {
      open('members.edit', { member, onSuccess });
    },

    suspend: () => {
      open('members.suspend', { member, onSuccess });
    },

    reactivate: () => {
      open('members.reactivate', { member, onSuccess });
    },

    remove: () => {
      open('members.remove', { member, onSuccess });
    },

    transferOwnership: () => {
      open('members.transferOwnership', { member, onSuccess });
    },
  };

  const name = toFullName(member);
  const phone = formatPhone(member.phone);
  const statusSince = new Date(member.statusChangedOn);

  useEffect(() => {
    if (!values.admin) {
      setFieldValue('adminAt', []);
    }

    if (!values.member) {
      setFieldValue('memberAt', []);
    }
  }, [values.admin, values.member, setFieldValue]);

  return (
    <Box sx={styles.fields}>
      <Box sx={styles.editIcon}>
        <MemberMenuCell key="menu" sx={styles.paddingEnd} member={member} actions={actions} />
      </Box>

      <Box sx={styles.memberDetails}>
        <UserAvatar name={name} size={80} />
        <Typography variant="subtitle1" fontWeight={600} sx={styles.paddingTop}>
          {name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {phone}
        </Typography>

        <Box sx={styles.paddingTop}>
          <MemberStatus sx="center" value={member.status} since={statusSince} />
        </Box>
      </Box>
      {/* TODO: Add The InfoBox (Ownership transfer in progress) here after the API is ready */}
      <MemberTable values={values} />
    </Box>
  );
};
